import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('wvc_session_id');
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('wvc_session_id', sessionId);
  }
  return sessionId;
};

// Check if this is a new visitor
const isNewVisitor = () => {
  const visited = localStorage.getItem('wvc_visited');
  if (!visited) {
    localStorage.setItem('wvc_visited', 'true');
    localStorage.setItem('wvc_first_visit', new Date().toISOString());
    return true;
  }
  return false;
};

// Determine referrer source
const getReferrerSource = () => {
  const referrer = document.referrer;
  if (!referrer) return 'direct';
  
  const referrerLower = referrer.toLowerCase();
  
  // Search engines
  if (referrerLower.includes('google.') || referrerLower.includes('bing.') || 
      referrerLower.includes('yahoo.') || referrerLower.includes('duckduckgo.')) {
    return 'search';
  }
  
  // Social media
  if (referrerLower.includes('facebook.') || referrerLower.includes('twitter.') ||
      referrerLower.includes('instagram.') || referrerLower.includes('linkedin.') ||
      referrerLower.includes('youtube.') || referrerLower.includes('whatsapp.')) {
    return 'social';
  }
  
  // Check if it's the same domain (internal)
  try {
    const referrerDomain = new URL(referrer).hostname;
    const currentDomain = window.location.hostname;
    if (referrerDomain === currentDomain) {
      return null; // Internal navigation, don't track as new session
    }
  } catch (e) {
    // Invalid URL
  }
  
  return 'referral';
};

// Fetch visitor location using free IP API
const fetchVisitorLocation = async () => {
  try {
    const response = await fetch('https://ip-api.com/json/?fields=status,country,regionName,city,timezone', {
      method: 'GET',
    });
    const data = await response.json();
    
    if (data.status === 'success') {
      return {
        city: data.city,
        region: data.regionName,
        country: data.country,
        timezone: data.timezone
      };
    }
  } catch (error) {
    console.log('[Analytics] Could not fetch location');
  }
  return null;
};

export const useAnalytics = () => {
  const location = useLocation();
  const sessionInitialized = useRef(false);
  const lastTrackedPath = useRef(null);

  // Initialize session on first load
  useEffect(() => {
    const initSession = async () => {
      if (sessionInitialized.current) return;
      sessionInitialized.current = true;

      const sessionId = getSessionId();
      const isNew = isNewVisitor();
      const referrerSource = getReferrerSource();
      
      // Only track new sessions (not internal navigation)
      if (referrerSource === null && !isNew) return;

      try {
        const locationData = await fetchVisitorLocation();
        
        await axios.post(`${API_URL}/analytics/session`, {
          session_id: sessionId,
          is_new_visitor: isNew,
          city: locationData?.city || null,
          country: locationData?.country || null,
          region: locationData?.region || null,
          timezone: locationData?.timezone || null,
          referrer_source: referrerSource || 'direct'
        });
        
        console.log('[Analytics] Session tracked:', sessionId);
      } catch (error) {
        console.log('[Analytics] Failed to track session');
      }
    };

    initSession();
  }, []);

  // Track page views on route change
  useEffect(() => {
    const trackPageView = async () => {
      const currentPath = location.pathname;
      
      // Don't track same path twice in a row
      if (lastTrackedPath.current === currentPath) return;
      lastTrackedPath.current = currentPath;

      const sessionId = getSessionId();

      try {
        await axios.post(`${API_URL}/analytics/pageview`, {
          session_id: sessionId,
          page: currentPath,
          referrer: document.referrer,
          user_agent: navigator.userAgent,
          screen_width: window.innerWidth,
          screen_height: window.innerHeight
        });
        
        console.log('[Analytics] Page view tracked:', currentPath);
      } catch (error) {
        console.log('[Analytics] Failed to track page view');
      }
    };

    trackPageView();
  }, [location.pathname]);

  // Function to track helpful feedback
  const trackHelpfulFeedback = useCallback(async (isHelpful, reportId = null, venueName = null) => {
    const sessionId = getSessionId();
    
    try {
      await axios.post(`${API_URL}/feedback/submit`, {
        session_id: sessionId,
        page: location.pathname,
        report_id: reportId,
        venue_name: venueName,
        feedback_type: 'helpful',
        is_helpful: isHelpful
      });
      
      console.log('[Analytics] Helpful feedback tracked');
      return true;
    } catch (error) {
      console.log('[Analytics] Failed to track feedback');
      return false;
    }
  }, [location.pathname]);

  // Function to submit feature request
  const submitFeatureRequest = useCallback(async (featureText) => {
    const sessionId = getSessionId();
    
    try {
      await axios.post(`${API_URL}/feedback/submit`, {
        session_id: sessionId,
        page: location.pathname,
        feedback_type: 'feature_request',
        feature_text: featureText
      });
      
      console.log('[Analytics] Feature request submitted');
      return true;
    } catch (error) {
      console.log('[Analytics] Failed to submit feature request');
      return false;
    }
  }, [location.pathname]);

  return {
    trackHelpfulFeedback,
    submitFeatureRequest,
    sessionId: getSessionId()
  };
};

export default useAnalytics;
